import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CalculationHistory } from '@/components/CalculationHistory';
import { useCalculationHistory } from '@/hooks/useCalculationHistory';
import { Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export const TipCalculator: React.FC = () => {
  const [billAmount, setBillAmount] = useState<string>('');
  const [tipPercentage, setTipPercentage] = useState<string>('');
  const [numberOfPeople, setNumberOfPeople] = useState<string>('1');
  const [result, setResult] = useState<{ totalTip: number; totalBill: number; splitAmount: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const { history, favorites, addCalculation, toggleFavorite, clearHistory } = useCalculationHistory();
  const { toast } = useToast();

  const calculate = (shouldAddToHistory = false) => {
    const bill = parseFloat(billAmount);
    const tip = parseFloat(tipPercentage);
    const people = parseInt(numberOfPeople);
    
    if (!isNaN(bill) && !isNaN(tip) && !isNaN(people) && bill > 0 && tip >= 0 && people > 0) {
      const totalTip = (tip / 100) * bill;
      const totalBill = bill + totalTip;
      const splitAmount = totalBill / people;
      const calculatedResult = { totalTip, totalBill, splitAmount };
      setResult(calculatedResult);
      
      // Add to history only when explicitly requested
      if (shouldAddToHistory) {
        addCalculation(
          'tip',
          { billAmount: bill, tipPercentage: tip, numberOfPeople: people },
          `$${calculatedResult.splitAmount.toFixed(2)} per person (${people} people)`
        );
      }
    } else {
      setResult(null);
    }
  };

  // Real-time calculation as user types
  useEffect(() => {
    calculate();
  }, [billAmount, tipPercentage, numberOfPeople]);

  // Add to history with debounce when user stops typing
  useEffect(() => {
    if (!billAmount || !tipPercentage) return;
    
    const timer = setTimeout(() => {
      const bill = parseFloat(billAmount);
      const tip = parseFloat(tipPercentage);
      const people = parseInt(numberOfPeople);
      if (!isNaN(bill) && !isNaN(tip) && !isNaN(people) && bill > 0 && tip >= 0 && people > 0) {
        calculate(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [billAmount, tipPercentage, numberOfPeople]);

  const copyToClipboard = async () => {
    if (!result) return;
    
    const people = parseInt(numberOfPeople);
    const textToCopy = people > 1 
      ? `Total: $${result.totalBill.toFixed(2)} | Per Person: $${result.splitAmount.toFixed(2)} (${people} people)`
      : `Total: $${result.totalBill.toFixed(2)} | Tip: $${result.totalTip.toFixed(2)}`;
    
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      toast({
        title: "Copied to clipboard!",
        description: "The tip calculation result has been copied.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="shadow-card">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-card-foreground">
          Tip Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="billAmount" className="text-sm font-medium">
              Bill Amount ($)
            </Label>
            <Input
              id="billAmount"
              type="number"
              placeholder="Enter bill amount"
              value={billAmount}
              onChange={(e) => setBillAmount(e.target.value)}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="tipPercentage" className="text-sm font-medium">
              Tip Percentage (%)
            </Label>
            <Input
              id="tipPercentage"
              type="number"
              placeholder="Enter tip %"
              value={tipPercentage}
              onChange={(e) => setTipPercentage(e.target.value)}
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="numberOfPeople" className="text-sm font-medium">
              Number of People
            </Label>
            <Input
              id="numberOfPeople"
              type="number"
              placeholder="Enter number"
              value={numberOfPeople}
              onChange={(e) => setNumberOfPeople(e.target.value)}
              className="h-12"
              min="1"
            />
          </div>
        </div>
        
        {result !== null && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg shadow-result">
              <p className="text-sm text-muted-foreground mb-1">Total Tip:</p>
              <p className="text-xl font-bold text-primary">
                ${result.totalTip.toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-secondary border border-border rounded-lg">
              <p className="text-sm text-muted-foreground mb-1">Total Bill:</p>
              <p className="text-xl font-semibold text-foreground">
                ${result.totalBill.toFixed(2)}
              </p>
            </div>
            <div className="p-4 bg-accent/10 border border-accent/20 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Per Person:</p>
                  <p className="text-xl font-semibold text-accent">
                    ${result.splitAmount.toFixed(2)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyToClipboard}
                  className="text-muted-foreground hover:text-accent p-1.5 h-auto"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <CalculationHistory
          history={history.filter(item => item.type === 'tip')}
          favorites={favorites}
          onToggleFavorite={toggleFavorite}
          onClearHistory={clearHistory}
        />
      </CardContent>
    </Card>
  );
};